import React, { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useParams, useSearchParams } from 'react-router';
import { GetOrganizationDetailDocument, GetOrganizationDetailQuery } from 'graphql/queries.generated';
import EventCard from 'components/EventCard';
import { Link } from 'react-router';
import { IsNotMember } from 'components/IsNotMember';
import { HasPermission } from 'components/HasPermission';
import { Permission } from 'app-constants';
import Image from 'components/Image';
import { ImageOwnerType, ImageSize } from 'app-constants';
import styles from 'pages/GroupDetail.module.css';
import globalStyles from 'Global.module.css';
import { IsMember } from 'components/IsMember';

const GroupDetail: React.FC = () => {
    const { organizationId } = useParams<{ organizationId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeSection = (searchParams.get('section') as 'aboutUs' | 'events' | 'members') || 'aboutUs';

    const setActiveSection = (section: 'aboutUs' | 'events' | 'members') => {
        setSearchParams((params) => {
            params.set('section', section);
            return params;
        });
    };

    // TODO: Different query that doesn't include members if the user is not a member
  const { loading, error, data } = useQuery<GetOrganizationDetailQuery>(GetOrganizationDetailDocument, {
    variables: {
      organizationId: organizationId || ''
    },
    skip: !organizationId
  });

  if (!organizationId) {
    return (
      <div className={styles.groupDetail}>
        <p className={styles.error}>No organization ID provided</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.groupDetail}>
        <p className={styles.loading}>Loading organization details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.groupDetail}>
        <p className={styles.error}>Error loading organization: {error.message}</p>
      </div>
    );
  }

  const organization = data?.organization;

  if (!organization) {
    return (
      <div className={styles.groupDetail}>
        <p className={styles.error}>Organization not found</p>
      </div>
    );
  }

  return (
    <div className={styles.groupDetail}>
          <div className={styles.groupPicture}><Image imageOwnerType={ImageOwnerType.Organization} imageSize={ImageSize.Banner} imageId={organization.imageId} /></div>
      <div className={styles.basicInfo}>
        <h1>{organization.name}</h1>
        {organization.region && (
          <p>{organization.region}</p>
        )}
        <p>{organization.memberCount} Member{organization.memberCount === 1 ? '' : 's'}</p>
      </div>
      <div className="controls">
              <HasPermission groupId={organization.organizationId} permission={Permission.MANAGE_GROUP}>
                  <Link className={styles.controlLink} to={`/groups/${organization.organizationId}/settings`}>
            Manage Group
          </Link>
        </HasPermission>
        <HasPermission groupId={organization.organizationId} permission={Permission.MANAGE_EVENTS}>
                  <Link className={styles.controlLink} to={`/groups/${organization.organizationId}/events/new`}>
            Create Event
          </Link>
        </HasPermission>
              <IsMember organizationId={organization.organizationId}>
                  <Link className={styles.controlLink} to={`/groups/${organization.organizationId}/members/me/edit`}>
                      Edit Member Profile
                  </Link>
              </IsMember>
              <IsNotMember organizationId={organization.organizationId}>
                  <Link className={styles.controlLink} to={`/groups/${organization.organizationId}/join`}>
                      Join Organization
                  </Link>
              </IsNotMember>
      </div>
      <div className={styles.organizers}>
        <h2>Organizers</h2>
        <div>
          {organization.leaders.map((leader) => (
              <Link key={leader.organizationMemberId} to={`/groups/${organization.organizationId}/members/${leader.organizationMemberId}`}>
                  <Image imageOwnerType={ImageOwnerType.Person} imageSize={ImageSize.Thumbnail} imageId={leader.combinedProfile.imageId} className={globalStyles.profileThumbnail} title={leader.combinedProfile.username} />
              </Link>
          ))}
        </div>
      </div>
      <nav className={styles.groupNav}>
        <button onClick={() => setActiveSection('aboutUs')}>About Us</button>
        <button onClick={() => setActiveSection('events')}>Events</button>
        <button onClick={() => setActiveSection('members')}>Members</button>
      </nav>
      <div className={styles.groupContent}>
        {activeSection === 'aboutUs' && (
          <section id="aboutUs" className={styles.aboutUs}>
            {organization.description}
          </section>
        )}
        {activeSection === 'events' && (
          <section id="upcomingEvents" className={styles.upcomingEvents}>
            {organization.upcomingEvents.map((event) => (
              <EventCard key={event.eventId} event={event} />
            ))}
          </section>
        )}
        {activeSection === 'members' && (
          <section id="members" className={styles.members}>
            <p>Members</p>
                      <IsMember organizationId={organization.organizationId}>
                          {organization.members.map((member) => (
                              <Link key={member.organizationMemberId} to={`/groups/${organization.organizationId}/members/${member.organizationMemberId}`}>
                                  <Image imageOwnerType={ImageOwnerType.Person} imageSize={ImageSize.Thumbnail} imageId={member.combinedProfile.imageId} className={globalStyles.profileThumbnail} title={member.combinedProfile.username} />
                              </Link>
                          ))}
                      </IsMember>
                      <IsNotMember organizationId={organization.organizationId}>
                          <p>Join this group to see a list of members</p>
                      </IsNotMember>
          </section>
        )}
      </div>

      {/* {organization.tags && organization.tags.length > 0 && (
        <div className={styles.organizationTags}>
          {organization.tags.filter((tag): tag is string => tag !== null).map((tag, idx) => (
            <span key={idx} className={styles.tag}>{tag}</span>
          ))}
        </div>
      )} */}

    </div>
  );
};

export default GroupDetail;
